import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SCOPE_LEVEL_UNITORUNITGROUP } from '../../shared/AppConstants';
import { checkedScope, isScopeAnyGlobalType } from '../../shared/UtilFunctions';
import {
  selectScopeDirty,
  selectScopeType,
  selectSelectedScopeUnits,
  setScopeInvalidError
} from './scopeSlice';

/**
 * Reacts to scope state changes in redux and keeps react-hook-form in sync
 * while maintaining unit-selection validation. The resolved error is written
 * back to `scope.scopeInvalidError` in the store so any component can read
 * it via `selectScopeInvalidError`.
 *
 * @param {object} params
 * @param {Function} params.setValue        - react-hook-form setValue
 * @param {Function} [params.onScopeChange] - optional callback(scopeType, selectedScopeUnits)
 *                                            invoked first when the scope becomes dirty
 *                                            (use for side-effects like SDF step management)
 */
export function useScopeValidation({ setValue, onScopeChange }) {
  const dispatch = useDispatch();
  const scopeType = useSelector(selectScopeType);
  const selectedScopeUnits = useSelector(selectSelectedScopeUnits);
  const scopeDirty = useSelector(selectScopeDirty);
  const hasSeenCleanScopeRef = useRef(false);

  useEffect(() => {
    if (!scopeDirty) {
      hasSeenCleanScopeRef.current = true;
      return;
    }
    if (!hasSeenCleanScopeRef.current) return;
    onScopeChange?.(scopeType, selectedScopeUnits);
    setValue('scope', scopeType, { shouldValidate: true });

    if (isScopeAnyGlobalType(scopeType)) {
      dispatch(setScopeInvalidError(null));
      return;
    }

    if (scopeType === SCOPE_LEVEL_UNITORUNITGROUP) {
      if (!selectedScopeUnits || selectedScopeUnits.length === 0) {
        dispatch(setScopeInvalidError('At least 1 Unit must be selected'));
        return;
      }
      const { newSelectedParent, newSelectedChild } = checkedScope(
        selectedScopeUnits,
        SCOPE_LEVEL_UNITORUNITGROUP
      );
      if (newSelectedParent.length === 0 && newSelectedChild.length === 0) {
        dispatch(setScopeInvalidError('At least 1 Unit must be selected'));
      } else {
        dispatch(setScopeInvalidError(null));
      }
    } else if (scopeType) {
      // non-UNITORUNITGROUP scope type (e.g. global) — no unit error needed
      dispatch(setScopeInvalidError(null));
    } else {
      // scopeType null with scopeDirty true = user actively cleared all selections
      dispatch(setScopeInvalidError('At least 1 Unit must be selected'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeType, selectedScopeUnits, scopeDirty]);
}
